import { useRef, useState } from 'react';
import { Badge, IconButton, Stack, Tooltip } from '@mui/material';
import Iconify from 'src/components/iconify';
import { useCampaignLeaksT } from './i18n';

const ICON_SX = {
  width: 26,
  height: 26,
  minWidth: 26,
  padding: '4px',
  borderRadius: '6px',
  color: '#919EAB',
  '&:hover': { backgroundColor: 'rgba(145, 158, 171, 0.08)' },
};

export type GridPanel = 'columns' | 'filters';

/**
 * The columns and filter panels open right below their button; clicking the button of the open
 * panel closes it (same pattern as the Non Brand search terms grid).
 */
export const useGridPanels = (apiRef: React.MutableRefObject<any>) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const onButtonDown = (panel: GridPanel) => {
    const state = apiRef.current?.state?.preferencePanel;
    wasOpen.current = !!(state?.open && state?.openedPanelValue === panel);
  };
  const onToggle = (panel: GridPanel, button: HTMLElement) => {
    if (wasOpen.current) {
      apiRef.current?.hidePreferences();
      return;
    }
    setAnchor(button);
    if (panel === 'columns') apiRef.current?.showPreferences('columns');
    else apiRef.current?.showFilterPanel();
  };
  const panelSlotProps = anchor ? { panel: { anchorEl: anchor, placement: 'bottom-end' as const } } : {};
  return { onButtonDown, onToggle, panelSlotProps };
};

type GridActionsProps = {
  activeFilters: number;
  onButtonDown: (panel: GridPanel) => void;
  onToggle: (panel: GridPanel, button: HTMLElement) => void;
  onExport: () => void;
  exportDisabled?: boolean;
};

/** Columns, filters (with the number of active filters) and CSV export icons. */
export default function GridActions({ activeFilters, onButtonDown, onToggle, onExport, exportDisabled }: GridActionsProps) {
  const ct = useCampaignLeaksT();
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Tooltip title={ct('toolbar.columns')} placement="top">
        <IconButton
          aria-label={ct('toolbar.columns')}
          size="small"
          onMouseDown={() => onButtonDown('columns')}
          onClick={(event) => onToggle('columns', event.currentTarget)}
          sx={ICON_SX}
        >
          <Iconify icon="mingcute:calendar-week-line" width={18} />
        </IconButton>
      </Tooltip>
      <Tooltip title={ct('toolbar.filters')} placement="top">
        <IconButton
          aria-label={ct('toolbar.filters')}
          size="small"
          onMouseDown={() => onButtonDown('filters')}
          onClick={(event) => onToggle('filters', event.currentTarget)}
          sx={ICON_SX}
        >
          <Badge badgeContent={activeFilters} color="primary" data-testid="filters-badge">
            <Iconify icon="ic:round-filter-list" width={18} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Tooltip title={ct('toolbar.export')} placement="top">
        <span>
          <IconButton aria-label={ct('toolbar.export')} size="small" disabled={exportDisabled} onClick={onExport} sx={ICON_SX}>
            <Iconify icon="tabler:download" width={18} />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}
